import { toRecentSummary } from './recentSummary';

import type { DiagnosisRecord } from '../onboarding/record';

/**
 * trendSeries — 저장된 회차들 → 홈의 "최근 보컬 추세" 7일 시계열(순수 변환).
 *
 * 이력 화면의 `historyRows`는 **직전 회차 대비 delta**만 낸다. 그건 "지난번보다 나아졌나"에
 * 답하지 "요 며칠 어땠나"에는 못 답한다 — 회차 사이 간격을 안 보기 때문에 하루에 세 번 잰
 * 날과 사흘 쉰 날이 같은 간격으로 늘어선다. 시계열은 **날**을 축으로 놓아야 해서 셀렉터를
 * 따로 둔다.
 *
 * 값의 출처는 `recentSummary`다(음역 제외 — 거긴 라벨만 있다). 홈 안에서 종합 점수 산식이
 * 갈리면 같은 회차가 요약 카드에서 78, 추세 마지막 점에서 79가 된다.
 *
 * 그 정본을 그대로 쓰는 대가가 하나 있다: 종합은 **그 회차에 저장된 축의** 균등 평균이라
 * 분모가 회차마다 다르다. 노래 진단을 건너뛴 회차(2축)와 다 마친 회차(4축)가 이웃하면 선이
 * 튀는데, 그건 목소리가 아니라 무엇을 쟀나가 바뀐 것이다. 그래도 여기서 정규화하지 않는다 —
 * 고치는 순간 홈의 두 숫자가 갈리고, 그게 이 셀렉터가 막으려던 바로 그 일이다.
 *
 * React·저장소 IO 의존 없음 — vitest 대상.
 */

/** 덱 p8의 토글 순서 — 종합 · 호흡 · 음역 · 음정. */
const TREND_AXES = ['overall', 'sustain', 'range', 'pitchAccuracy'] as const;

export type TrendAxis = (typeof TREND_AXES)[number];

/**
 * 고를 수 있는 창 길이(일). 시안 §7.1의 `최근 7일 ∨`이 드롭다운이라 선택지가 있어야 한다 —
 * 옵션 하나짜리 드롭다운은 죽은 버튼이다.
 *
 * 30일까지만 두는 이유: 저장 상한이 50회차라(`MAX_HISTORY`) 더 긴 창은 칸만 늘고 값은
 * 안 늘어난다. 하루 한 번 재는 사람에게 30칸이 이미 상한 근처다.
 */
export const TREND_WINDOWS = [7, 30] as const;

export type TrendWindow = (typeof TREND_WINDOWS)[number];

/** 기본 창. 시안이 처음 보여주는 값이다. */
export const TREND_DAYS: TrendWindow = 7;

/** 선을 그리려면 점이 둘은 있어야 한다 — 하나면 화면은 안내 문구를 낸다. */
export const MIN_TREND_POINTS = 2;

export interface TrendPoint {
  /** 그 칸의 **로컬 자정** epoch ms. 결측 판정과 X축 위치의 키. */
  dayStart: number;
  /** `8/4` — 축에 찍는 짧은 날짜. */
  label: string;
  /** 그날 최신 회차의 값. 회차가 없거나 그 축을 안 쟀으면 null(0 아님). */
  val: number | null;
}

/** 값이 있는 날. 화면이 같은 null 검사를 두 번 하지 않게 좁혀 둔다. */
export type FilledTrendPoint = TrendPoint & { val: number };

export interface TrendScale {
  max: number;
  ticks: number[];
}

export interface TrendSeries {
  axis: TrendAxis;
  /** 창 길이만큼, 과거→현재. 결측일도 칸은 있다(val이 null). */
  points: TrendPoint[];
  /** **값이 있는 날** 수. 회차 수가 아니다 — 하루에 세 번 재도 점은 하나다. */
  filled: number;
  scale: TrendScale;
  /** 값이 있는 첫 날 — 향상 폭의 기준. */
  first: FilledTrendPoint | null;
  /** 값이 있는 마지막 날. 텍스트 대안이 읽는 "최근 값". */
  latest: FilledTrendPoint | null;
}

const SCORE_SCALE: TrendScale = { max: 100, ticks: [0, 25, 50, 75, 100] };

/**
 * 축별 Y 눈금.
 *
 * 음역만 자를 따로 쓴다. 그건 점수가 아니라 **반음 폭**이라 0~100에 정규화하면 "32반음"이
 * "32점"으로 읽히고, 같은 화면의 다른 탭들과 나란히 놓이면서 그 오독이 굳는다.
 *
 * 상한 48반음은 미감이 아니라 검출기에서 나온 값이다: 기본 f0 대역이 65~1100Hz라
 * (`libs/shared/src/types/pitch-tracker.config.ts:75-76`) 잴 수 있는 최대 폭이
 * 12·log2(1100/65) ≈ 49반음이다. 그보다 넓은 폭은 애초에 측정될 수 없으므로 데이터에 맞춰
 * 자동 확대/축소하지 않는다 — 자동 눈금은 회차마다 자가 바뀌어 잡음을 성장처럼 보이게 한다.
 * 눈금 간격은 옥타브(12반음).
 */
export const TREND_SCALE: Record<TrendAxis, TrendScale> = {
  overall: SCORE_SCALE,
  sustain: SCORE_SCALE,
  pitchAccuracy: SCORE_SCALE,
  range: { max: 48, ticks: [0, 12, 24, 36, 48] },
};

const startOfLocalDay = (epochMs: number): number => {
  const d = new Date(epochMs);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/**
 * 오늘까지 `TREND_DAYS`칸, 과거→현재. 하루를 86400000ms로 빼지 않는다 — DST가 있는
 * 지역에서 그 뺄셈은 하루가 23/25시간인 날에 칸을 어긋나게 한다. 날짜 필드로 센다.
 */
const dayStarts = (now: number, days: number): number[] => {
  const today = new Date(now);
  return Array.from({ length: days }, (_, i) =>
    new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - (days - 1 - i),
    ).getTime(),
  );
};

const dayLabel = (dayStart: number): string => {
  const d = new Date(dayStart);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/**
 * 회차에서 축 값 하나. 점수축은 `recentSummary`를 거친다 — 홈의 두 화면이 같은 산식을 쓰게
 * 하는 게 이 우회의 전부다. 음역은 요약에 라벨(`E2 ~ C5`)로만 있어 회차에서 직접 읽는다.
 */
const axisValue = (record: DiagnosisRecord, axis: TrendAxis): number | null => {
  if (axis === 'range') {
    return record.range?.semitones ?? null;
  }
  const summary = toRecentSummary(record);
  if (!summary) {
    return null;
  }
  return axis === 'overall'
    ? summary.overall
    : (summary.scores.find((s) => s.key === axis)?.val ?? null);
};

/** 날짜칸 → 그날 **최신** 회차. 평균이 아니다 — 회차는 측정이지 표본이 아니다. */
const latestByDay = (
  records: DiagnosisRecord[],
  days: number[],
): Map<number, DiagnosisRecord> => {
  const picked = new Map<number, DiagnosisRecord>();
  for (const record of records) {
    const day = startOfLocalDay(record.completedAt);
    // 창 밖(오래됐거나 미래 시각)이면 칸이 없다.
    if (!days.includes(day)) {
      continue;
    }
    const kept = picked.get(day);
    if (!kept || record.completedAt > kept.completedAt) {
      picked.set(day, record);
    }
  }
  return picked;
};

export const buildTrendSeries = ({
  records,
  axis,
  now,
  windowDays = TREND_DAYS,
}: {
  /** 저장소가 내는 그대로(최신순) — 순서에 기대지 않는다. */
  records: DiagnosisRecord[];
  axis: TrendAxis;
  /** epoch ms를 호출자가 준다 — 테스트 결정성(`toDiagnosisRecord`와 같은 규약). */
  now: number;
  /** 창 길이(일). 안 주면 시안의 기본값인 7일이다. */
  windowDays?: TrendWindow;
}): TrendSeries => {
  const days = dayStarts(now, windowDays);
  const byDay = latestByDay(records, days);

  const points: TrendPoint[] = days.map((dayStart) => {
    const record = byDay.get(dayStart);
    return {
      dayStart,
      label: dayLabel(dayStart),
      val: record ? axisValue(record, axis) : null,
    };
  });

  const filledPoints = points.filter(
    (p): p is FilledTrendPoint => p.val !== null,
  );
  return {
    axis,
    points,
    filled: filledPoints.length,
    scale: TREND_SCALE[axis],
    first: filledPoints[0] ?? null,
    latest: filledPoints[filledPoints.length - 1] ?? null,
  };
};
